import * as React from 'react';
import { Send } from 'lucide-react';
import { cn } from '../../lib/cn';
import { Textarea } from './textarea';
import { Button } from './button';

/**
 * Composer — the chat message-input row of the conversation layer: an
 * auto-growing `Textarea` with an inline send `Button`. Controlled (the host
 * owns the draft so it can clear optimistically / restore on failure).
 *
 * Interaction:
 *   • Enter (no Shift, not mid-IME-composition) submits; Shift+Enter inserts a
 *     newline.
 *   • Clicking the send button submits.
 *   • The send button is disabled while `disabled` (e.g. balance exhausted),
 *     `busy` (a turn is in flight), or the trimmed draft is empty.
 *
 * Copy is threaded via a typed `labels` prop with German defaults (ACKERDOKU is
 * a German product) — the DS ships no i18n runtime, the host passes localized
 * strings. `data-testid` / other attrs reach the controls via `textareaProps`
 * and `sendButtonProps`.
 */
export interface ComposerLabels {
  /** Textarea placeholder. */
  placeholder?: string;
  /** Accessible name + tooltip for the send button. */
  send?: string;
}

const defaultLabels: Required<ComposerLabels> = {
  placeholder: 'Nachricht schreiben…',
  send: 'Senden',
};

// React's *HTMLAttributes interfaces don't type `data-*` keys on a plain object
// literal (JSX handles them specially), so allow them explicitly on the
// pass-through prop bags below (e.g. a consumer's `data-testid`).
type DataAttributes = Record<`data-${string}`, string | number | boolean | undefined>;

export interface ComposerProps {
  value: string;
  onValueChange: (value: string) => void;
  /** Fired on Enter or the send button — never with an empty/whitespace draft. */
  onSubmit: () => void;
  /** Hard-disable the input + send (e.g. balance exhausted). */
  disabled?: boolean;
  /** A turn is in flight — keep the input editable but block another send. */
  busy?: boolean;
  labels?: ComposerLabels;
  /** Grow bounds for the textarea (rows). Default 1..5. */
  minRows?: number;
  maxRows?: number;
  className?: string;
  textareaProps?: Omit<
    React.TextareaHTMLAttributes<HTMLTextAreaElement>,
    'value' | 'onChange' | 'onKeyDown' | 'disabled' | 'placeholder' | 'rows'
  > &
    DataAttributes;
  sendButtonProps?: Omit<
    React.ButtonHTMLAttributes<HTMLButtonElement>,
    'onClick' | 'disabled' | 'type'
  > &
    DataAttributes;
}

export const Composer = React.forwardRef<HTMLTextAreaElement, ComposerProps>(
  (
    {
      value,
      onValueChange,
      onSubmit,
      disabled = false,
      busy = false,
      labels,
      minRows = 1,
      maxRows = 5,
      className,
      textareaProps,
      sendButtonProps,
    },
    forwardedRef
  ) => {
    const l = { ...defaultLabels, ...labels };
    const innerRef = React.useRef<HTMLTextAreaElement | null>(null);
    const setRefs = React.useCallback(
      (node: HTMLTextAreaElement | null) => {
        innerRef.current = node;
        if (typeof forwardedRef === 'function') forwardedRef(node);
        else if (forwardedRef) forwardedRef.current = node;
      },
      [forwardedRef]
    );

    // Auto-grow: reset to natural height, then clamp to [minRows, maxRows] worth
    // of lines. Runs on every value change (mirrors the app's useAutoGrowTextarea
    // so the DS composer owns its own sizing without an app hook).
    React.useLayoutEffect(() => {
      const el = innerRef.current;
      if (!el) return;
      const style = window.getComputedStyle(el);
      const lineHeight = parseFloat(style.lineHeight) || 20;
      const paddingY =
        (parseFloat(style.paddingTop) || 0) +
        (parseFloat(style.paddingBottom) || 0);
      const borderY =
        (parseFloat(style.borderTopWidth) || 0) +
        (parseFloat(style.borderBottomWidth) || 0);
      const minH = minRows * lineHeight + paddingY + borderY;
      const maxH = maxRows * lineHeight + paddingY + borderY;
      el.style.height = 'auto';
      const next = Math.min(Math.max(el.scrollHeight, minH), maxH);
      el.style.height = `${next}px`;
      el.style.overflowY = el.scrollHeight > maxH ? 'auto' : 'hidden';
    }, [value, minRows, maxRows]);

    const canSend = !disabled && !busy && value.trim().length > 0;

    const submit = () => {
      if (!canSend) return;
      onSubmit();
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (
        event.key === 'Enter' &&
        !event.shiftKey &&
        !event.nativeEvent.isComposing
      ) {
        event.preventDefault();
        submit();
      }
    };

    return (
      <div className={cn('tw:flex tw:items-end tw:gap-2', className)}>
        <Textarea
          {...textareaProps}
          ref={setRefs}
          value={value}
          placeholder={l.placeholder}
          disabled={disabled}
          rows={minRows}
          onChange={(event) => onValueChange(event.target.value)}
          onKeyDown={handleKeyDown}
        />
        <Button
          {...sendButtonProps}
          type="button"
          size="icon"
          className={cn('tw:shrink-0 tw:rounded-full', sendButtonProps?.className)}
          aria-label={l.send}
          title={l.send}
          disabled={!canSend}
          onClick={submit}
        >
          <Send className="tw:h-4 tw:w-4" aria-hidden />
        </Button>
      </div>
    );
  }
);
Composer.displayName = 'Composer';
